import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { SectionCard } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ActiveDeliveryCard, ACTIVE_DELIVERY_STATUSES } from "@/components/ActiveDeliveryCard";
import { OnboardingGuide, ONBOARDING_KEY } from "@/components/OnboardingGuide";
import {
  Package, Plane, Search, PackageSearch, PlusCircle, ArrowRight, Bell, MessageSquare, MapPin, Activity, HelpCircle,
} from "lucide-react";

const QUICK = [
  { label: "Send a Package", icon: PlusCircle, to: "/create-delivery", testid: "quick-create-delivery", color: "bg-primary text-white" },
  { label: "Post a Trip", icon: Plane, to: "/create-trip", testid: "quick-create-trip", color: "bg-sky-500 text-white" },
  { label: "Find a Traveller", icon: Search, to: "/find-travellers", testid: "quick-find-travellers", color: "bg-white text-slate-800 border border-slate-200" },
  { label: "Find a Delivery", icon: PackageSearch, to: "/find-deliveries", testid: "quick-find-deliveries", color: "bg-white text-slate-800 border border-slate-200" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [convos, setConvos] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, d, n, c, a] = await Promise.all([
          api.get("/trips/mine"), api.get("/deliveries/mine"),
          api.get("/notifications"), api.get("/conversations"),
          api.get("/deliveries/assigned"),
        ]);
        setTrips(t.data.trips);
        setDeliveries(d.data.deliveries);
        setNotifs(n.data.notifications);
        setConvos(c.data.conversations);
        setAssigned(a.data.deliveries);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(ONBOARDING_KEY)) setGuideOpen(true);
  }, []);

  const senderActive = deliveries.find((d) => ACTIVE_DELIVERY_STATUSES.includes(d.status));
  const travellerActive = assigned.find((d) => ACTIVE_DELIVERY_STATUSES.includes(d.status));

  const upcomingTrips = trips.filter((t) => ["Upcoming", "Active"].includes(t.status));
  const activeDeliveries = deliveries.filter((d) => ["Requested", "Matched", "Accepted", "In Transit"].includes(d.status));
  const recent = [...notifs].slice(0, 6);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[0,1,2,3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OnboardingGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="dashboard-welcome">
            Welcome back, {user?.name?.split(" ")[0] || "there"} 👋
          </h1>
          <p className="mt-1 text-slate-500">Here's what's happening with your deliveries and trips.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setGuideOpen(true)} className="rounded-xl flex-shrink-0" data-testid="how-it-works-btn">
          <HelpCircle className="w-4 h-4" /> <span className="hidden sm:inline">How Pack Mate Works</span>
        </Button>
      </div>

      {(senderActive || travellerActive) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="active-delivery-section">
          {senderActive && <ActiveDeliveryCard delivery={senderActive} role="sender" onOpen={(id) => navigate(`/deliveries/${id}`)} />}
          {travellerActive && <ActiveDeliveryCard delivery={travellerActive} role="traveller" onOpen={(id) => navigate(`/deliveries/${id}`)} />}
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {QUICK.map((q) => (
          <button key={q.to} data-testid={q.testid} onClick={() => navigate(q.to)}
            className={`${q.color} rounded-2xl p-4 sm:p-5 flex flex-col items-start gap-3 shadow-sm hover:-translate-y-0.5 transition-all text-left`}>
            <q.icon className="w-6 h-6" />
            <span className="font-semibold text-sm sm:text-base">{q.label}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          <SectionCard title="Upcoming Trips" action={<Button variant="ghost" size="sm" onClick={() => navigate("/my-trips")} data-testid="see-trips">See all</Button>}>
            {upcomingTrips.length === 0 ? (
              <EmptyState icon={Plane} title="No upcoming trips yet." description="Publish a trip and start carrying packages along your route." actionLabel="Create your first trip" actionTo="/create-trip" testid="empty-trips" />
            ) : (
              <div className="space-y-3">
                {upcomingTrips.slice(0, 3).map((t) => (
                  <div key={t.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:bg-accent/50 transition-colors">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                        <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="truncate">{t.origin}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{t.destination}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{t.travel_date} · {t.capacity}</p>
                    </div>
                    <StatusBadge status={t.status} />
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Active Deliveries" action={<Button variant="ghost" size="sm" onClick={() => navigate("/my-deliveries")} data-testid="see-deliveries">See all</Button>}>
            {activeDeliveries.length === 0 ? (
              <EmptyState icon={Package} title="No active deliveries." description="Create a delivery request to get your package moving." actionLabel="Create a delivery" actionTo="/create-delivery" testid="empty-deliveries" />
            ) : (
              <div className="space-y-3">
                {activeDeliveries.slice(0, 4).map((d) => (
                  <button key={d.id} onClick={() => navigate(`/deliveries/${d.id}`)} data-testid={`dash-delivery-${d.id}`}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:bg-accent/50 transition-colors text-left">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{d.description}</p>
                      <p className="text-xs text-slate-400 mt-1">{d.pickup} → {d.destination}</p>
                    </div>
                    <StatusBadge status={d.status} />
                  </button>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <SectionCard title="Messages" action={<Button variant="ghost" size="sm" onClick={() => navigate("/messages")} data-testid="see-messages">Open</Button>}>
            {convos.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400"><MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />No conversations yet. Chats appear after a connection is accepted.</div>
            ) : (
              <div className="space-y-2">
                {convos.slice(0, 4).map((c) => (
                  <button key={c.id} onClick={() => navigate("/messages")} className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-accent/50 text-left">
                    <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-primary font-semibold text-sm">{c.other_user?.name?.[0] || "?"}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{c.other_user?.name}</p>
                      <p className="text-xs text-slate-400 truncate">{c.last_message?.text || "Say hello"}</p>
                    </div>
                    {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center">{c.unread}</span>}
                  </button>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Recent Activity">
            {recent.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400"><Activity className="w-8 h-8 mx-auto mb-2 text-slate-300" />No activity yet.</div>
            ) : (
              <div className="space-y-3">
                {recent.map((n) => (
                  <div key={n.id} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center flex-shrink-0"><Bell className="w-4 h-4 text-primary" /></div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800">{n.title}</p>
                      <p className="text-xs text-slate-400">{n.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
